import React, { useState, useMemo } from "react";
import { useAppSelector, useAppDispatch } from "../../../../store";
import {
  addInternalSystem,
  removeInternalSystem,
} from "../../../../store/features/optionalSystemsSlice";
import { InternalSystem } from "../../../../types/optionalSystems";
import { calculateInternalSystemSpecs } from "../../../../utils/calculations/optionalSystems";
import {
  formatCurrency,
  formatWeight,
  formatPower,
} from "../../../../utils/formatting";
import { v4 } from "uuid";
import { selectors } from "../../../../store/selectors";
import TonnageInput from "../../../common/TonnageInput";
import { SmallCraftAssignmentModal } from "./SmallCraftAssignmentModal";
const uuidv4 = v4;

interface InternalSystemsProps {
  availableSystems: InternalSystem[];
  shipTonnage: number;
  techLevel: number;
  isStreamlined?: boolean;
  isDispersedStructure?: boolean;
  tlOverrideActive?: boolean;
}

const InternalSystems: React.FC<InternalSystemsProps> = ({
  availableSystems,
  shipTonnage,
  techLevel,
  isStreamlined = false,
  isDispersedStructure = false,
  tlOverrideActive = false,
}) => {
  const dispatch = useAppDispatch();
  const systems = useAppSelector((state) => state.optionalSystems.internal);
  const [configSystem, setConfigSystem] = useState<InternalSystem | null>(null);

  // Get assigned crew count (prefer overrides, else minimums)
  const crewComposition = useAppSelector(
    selectors.shipBuilder.selectCrewComposition,
  );
  const minimumCrew = useAppSelector(selectors.shipBuilder.selectMinimumCrew);
  const crewOverrides = useAppSelector(
    selectors.shipBuilder.selectCrewOverrides,
  );
  // Use sum of all assigned crew roles (excluding 0/undefined)
  const assignedCrew = crewComposition
    ? Object.values(crewComposition).reduce(
        (sum: number, n) => sum + (typeof n === "number" ? n : 0),
        0,
      )
    : Object.values(minimumCrew).reduce(
        (sum: number, n) => sum + (typeof n === "number" ? n : 0),
        0,
      );
  // Get assigned marines from crewOverrides
  const assignedMarines = crewOverrides?.["Ship's Troops/Marines"] ?? 0;

  // Precompute selector results for special systems
  const internalArmouryStats = useAppSelector((state) => {
    const selector = selectors.derived.createInternalArmouryStatsSelector(
      assignedCrew,
      assignedMarines,
    );
    return selector(state);
  });
  // Grav Screen stats are now calculated automatically based on ship size
  // const gravScreenStats = useAppSelector(state => selectors.derived.selectGravScreenStats(state));

  // Capsule system definitions for dropdown
  const capsuleOptions = [
    availableSystems.find((s) => s.subtype === "internalReEntryCapsule"),
    availableSystems.find((s) => s.subtype === "internalAssaultCapsule"),
    availableSystems.find(
      (s) => s.subtype === "internalHighSurvivabilityCapsule",
    ),
    availableSystems.find((s) => s.subtype === "internalReEntryPod"),
  ].filter(Boolean);
  const [selectedCapsule, setSelectedCapsule] = useState<string>(
    capsuleOptions[0]?.subtype || "internalReEntryCapsule",
  );
  const [capsuleQty, setCapsuleQty] = useState<number>(1);
  const selectedCapsuleSystem = capsuleOptions.find(
    (s): s is InternalSystem => !!s && s.subtype === selectedCapsule,
  );

  const handleAddSystem = (system: InternalSystem) => {
    // Always allow adding multiple Additional Airlocks, Medical Bays, Booby Trap Airlocks, Briefing Rooms, Docking Spaces, Launch Tubes, Recovery Decks, Stables, and Workshops
    if (
      system.subtype === "internalAirlock" ||
      system.subtype === "internalMedicalBay" ||
      system.subtype === "internalBoobyTrap" ||
      system.subtype === "internalBriefingRoom" ||
      system.subtype === "internalDockingSpace" ||
      system.subtype === "internalLaunchTube" ||
      system.subtype === "internalRecoveryDeck" ||
      system.subtype === "internalStable" ||
      system.subtype === "internalWorkshop"
    ) {
      const newSystem = { ...system, id: uuidv4() };
      if (needsConfiguration(system)) {
        setConfigSystem(newSystem);
      } else {
        const configuredSystem = configureSystem(newSystem);
        dispatch(addInternalSystem(configuredSystem));
      }
      return;
    }

    // Special handling for Grav Screen - auto-calculate based on ship size
    if (system.subtype === "internalGravScreen") {
      const gravScreenTonnage = Math.max(1, Math.ceil(shipTonnage / 200)); // 1 ton per 200 tons hull (rounded up)
      const gravScreenCost = gravScreenTonnage * 1000000; // MCr1 per ton
      const gravScreenPower = gravScreenTonnage * 2; // 2 power per ton

      const configuredSystem = {
        ...system,
        id: uuidv4(),
        tonnage: gravScreenTonnage,
        cost: gravScreenCost,
        power: gravScreenPower,
        properties: {
          ...system.properties,
          tonnage: gravScreenTonnage,
        },
      };
      dispatch(addInternalSystem(configuredSystem));
      return;
    }

    // For other systems, keep existing logic
    if (needsConfiguration(system)) {
      setConfigSystem(system);
    } else {
      const configuredSystem = configureSystem(system);
      dispatch(addInternalSystem(configuredSystem));
    }
  };

  const handleRemoveSystem = (systemId: string) => {
    dispatch(removeInternalSystem(systemId));
  };

  const handleConfigureSystem = (system: InternalSystem) => {
    const configuredSystem = configureSystem(system);
    dispatch(addInternalSystem(configuredSystem));
    setConfigSystem(null);
  };

  const handleCancelConfig = () => {
    setConfigSystem(null);
  };

  const needsConfiguration = (system: InternalSystem): boolean => {
    return [
      "internalAirlock",
      "internalArmoury",
      "internalBiosphere",
      "internalBoobyTrap",
      "internalConcealedCompartment",
      "internalConstructionDeck",
      "internalDockingSpace",
      "internalFullHangar",
      "internalLaboratory",
      "internalLaunchTube",
      "internalRecoveryDeck",
      "internalStudio",
      "internalTrainingFacilities",
      "internalUnrepSystem",
      "internalVault",
    ].includes(system.subtype);
  };

  const configureSystem = (system: InternalSystem): InternalSystem => {
    // For now, just return the system (real config handled in config panel)
    return system;
  };

  const renderConfigurationPanel = () => {
    if (!configSystem) return null;
    switch (configSystem.subtype) {
      case "internalAirlock":
        return (
          <AirlockConfig
            system={configSystem}
            onConfigure={handleConfigureSystem}
            onCancel={handleCancelConfig}
          />
        );
      case "internalArmoury":
        return (
          <ArmouryConfig
            system={configSystem}
            onConfigure={handleConfigureSystem}
            onCancel={handleCancelConfig}
          />
        );
      case "internalBiosphere":
        return (
          <BiosphereConfig
            system={configSystem}
            onConfigure={handleConfigureSystem}
            onCancel={handleCancelConfig}
          />
        );
      case "internalBoobyTrap":
        return (
          <BoobyTrapConfig
            system={configSystem}
            onConfigure={handleConfigureSystem}
            onCancel={handleCancelConfig}
            shipTonnage={shipTonnage}
            installedSystems={systems}
          />
        );
      case "internalConcealedCompartment":
        return (
          <ConcealedCompartmentConfig
            system={configSystem}
            onConfigure={handleConfigureSystem}
            onCancel={handleCancelConfig}
            shipTonnage={shipTonnage}
          />
        );
      case "internalDockingSpace":
        return (
          <DockingSpaceConfig
            system={configSystem}
            onConfigure={handleConfigureSystem}
            onCancel={handleCancelConfig}
          />
        );
      case "internalFullHangar":
        return (
          <FullHangarConfig
            system={configSystem}
            onConfigure={handleConfigureSystem}
            onCancel={handleCancelConfig}
          />
        );
      case "internalLaboratory":
        return (
          <LaboratoryConfig
            system={configSystem}
            onConfigure={handleConfigureSystem}
            onCancel={handleCancelConfig}
          />
        );
      case "internalLaunchTube":
        return (
          <LaunchTubeConfig
            system={configSystem}
            onConfigure={handleConfigureSystem}
            onCancel={handleCancelConfig}
          />
        );
      case "internalRecoveryDeck":
        return (
          <RecoveryDeckConfig
            system={configSystem}
            onConfigure={handleConfigureSystem}
            onCancel={handleCancelConfig}
          />
        );
      case "internalConstructionDeck":
        return (
          <ConstructionDeckConfig
            system={configSystem}
            onConfigure={handleConfigureSystem}
            onCancel={handleCancelConfig}
          />
        );
      case "internalStudio":
        return (
          <StudioConfig
            system={configSystem}
            onConfigure={handleConfigureSystem}
            onCancel={handleCancelConfig}
          />
        );
      case "internalTrainingFacilities":
        return (
          <TrainingFacilitiesConfig
            system={configSystem}
            onConfigure={handleConfigureSystem}
            onCancel={handleCancelConfig}
          />
        );
      case "internalVault":
        return (
          <VaultConfig
            system={configSystem}
            onConfigure={handleConfigureSystem}
            onCancel={handleCancelConfig}
          />
        );
      // Add more config panels as needed for other types
      default:
        return (
          <GenericTonnageConfig
            system={configSystem}
            onConfigure={handleConfigureSystem}
            onCancel={handleCancelConfig}
          />
        );
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">Internal Systems</h2>
      <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6 bg-white dark:bg-gray-800">
        <h3 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-gray-100">
          Available Internal Systems
        </h3>

        {/* Grouped Systems */}
        {(() => {
          // Get all internal systems except capsules
          const allSystems = availableSystems.filter(
            (system) =>
              techLevel >= system.techLevel &&
              ![
                "internalReEntryCapsule",
                "internalAssaultCapsule",
                "internalHighSurvivabilityCapsule",
                "internalReEntryPod",
              ].includes(system.subtype),
          );

          // Define system groups
          const systemGroups: Record<string, string[]> = {
            "Emergency Systems": [], // Special handling for capsules
            "Access & Security": [
              "internalAirlock",
              "internalBoobyTrap",
              "internalConcealedCompartment",
              "internalVault",
            ],
            "Craft Operations": [
              "internalLaunchTube",
              "internalRecoveryDeck",
              "internalDockingSpace",
              "internalFullHangar",
              "internalConstructionDeck",
            ],
            "Crew Facilities": [
              "internalArmoury",
              "internalBriefingRoom",
              "internalLibrary",
              "internalTrainingFacilities",
            ],
            "Life Support & Accommodation": [
              "internalBiosphere",
              "internalStable",
              "internalMedicalBay",
            ],
            "Professional Spaces": [
              "internalLaboratory",
              "internalStudio",
              "internalWorkshop",
            ],
            "Specialized Systems": [
              "internalGravScreen",
              "internalUnrepSystem",
            ],
          };

          return Object.entries(systemGroups).map(
            ([groupName, systemSubtypes]) => {
              // Special handling for Emergency Systems (capsules)
              if (groupName === "Emergency Systems") {
                if (capsuleOptions.length === 0) return null;

                return (
                  <div key={groupName} className="mb-6">
                    <h4 className="text-lg font-semibold mb-3 text-blue-700 border-b border-blue-200 pb-2">
                      {groupName}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div className="border rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700 min-h-[220px]">
                        <div>
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                              Capsule
                            </h4>
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                              TL{selectedCapsuleSystem?.techLevel}
                            </span>
                          </div>
                          <select
                            className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm mb-2"
                            value={selectedCapsule}
                            onChange={(e) => setSelectedCapsule(e.target.value)}
                          >
                            {capsuleOptions
                              .filter((opt): opt is InternalSystem => !!opt)
                              .map((opt) => (
                                <option key={opt.subtype} value={opt.subtype}>
                                  {opt.name}
                                </option>
                              ))}
                          </select>
                          <div className="mb-2">
                            <TonnageInput
                              value={capsuleQty * 0.5} // Convert quantity to tons (0.5 tons per capsule)
                              onChange={(tons) => setCapsuleQty(Math.max(1, Math.ceil(tons / 0.5)))} // Convert tons back to quantity
                              systemType="reentryCapsule"
                              category="optionalSystems"
                              label="Total Tonnage"
                              placeholder="Enter tonnage"
                              className="text-sm"
                              showValidation={true}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Quantity: {capsuleQty} capsule{capsuleQty !== 1 ? 's' : ''} (0.5 tons each)
                            </p>
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                            <div>
                              <b>Cost:</b>{" "}
                              {formatCurrency(selectedCapsuleSystem?.cost ?? 0)}
                            </div>
                            <div>
                              <b>Tonnage:</b>{" "}
                              {formatWeight(
                                selectedCapsuleSystem?.tonnage ?? 0,
                              )}
                            </div>
                            <div>
                              <b>Power:</b>{" "}
                              {formatPower(selectedCapsuleSystem?.power ?? 0)}
                            </div>
                          </div>
                        </div>
                        <div className="mt-2">
                          <button
                            className="w-full mt-2 px-3 py-1 rounded text-white font-semibold bg-blue-600 hover:bg-blue-700"
                            onClick={() => {
                              for (let i = 0; i < capsuleQty; i++) {
                                const sys: InternalSystem = {
                                  ...selectedCapsuleSystem!,
                                  id: `${selectedCapsuleSystem?.id}_${Date.now()}_${i}`,
                                  type: "internal", // Ensure this is always set
                                };
                                dispatch(addInternalSystem(sys));
                              }
                            }}
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }

              const groupSystems = allSystems
                .filter((system) => systemSubtypes.includes(system.subtype))
                .sort((a, b) => a.name.localeCompare(b.name));

              if (groupSystems.length === 0) return null;

              return (
                <div key={groupName} className="mb-6">
                  <h4 className="text-lg font-semibold mb-3 text-blue-700 border-b border-blue-200 pb-2">
                    {groupName}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {groupSystems.map((system) => {
                      let specs = calculateInternalSystemSpecs(
                        system.subtype,
                        system.properties,
                      );
                      // Special handling for Armoury
                      if (system.subtype === "internalArmoury") {
                        specs = {
                          ...specs,
                          tonnage: internalArmouryStats.tonnage,
                          cost: internalArmouryStats.cost,
                          power: internalArmouryStats.power,
                        };
                      }
                      // Special handling for Grav Screen - auto-calculate based on ship size
                      if (system.subtype === "internalGravScreen") {
                        const gravScreenTonnage = Math.max(
                          1,
                          Math.ceil(shipTonnage / 200),
                        ); // 1 ton per 200 tons hull (rounded up)
                        const gravScreenCost = gravScreenTonnage * 1000000; // MCr1 per ton
                        const gravScreenPower = gravScreenTonnage * 2; // 2 power per ton
                        specs = {
                          ...specs,
                          tonnage: gravScreenTonnage,
                          cost: gravScreenCost,
                          power: gravScreenPower,
                        };
                      }

                      const isAdditionalAirlock =
                        system.subtype === "internalAirlock";
                      const isMedicalBay =
                        system.subtype === "internalMedicalBay";
                      const isBoobyTrap =
                        system.subtype === "internalBoobyTrap";
                      const isBriefingRoom =
                        system.subtype === "internalBriefingRoom";
                      const isDockingSpace =
                        system.subtype === "internalDockingSpace";
                      const isLaunchTube =
                        system.subtype === "internalLaunchTube";
                      const isRecoveryDeck =
                        system.subtype === "internalRecoveryDeck";
                      const isStable = system.subtype === "internalStable";
                      const isWorkshop = system.subtype === "internalWorkshop";
                      const isGravScreen =
                        system.subtype === "internalGravScreen";
                      const isInstalled =
                        !(
                          isAdditionalAirlock ||
                          isMedicalBay ||
                          isBoobyTrap ||
                          isBriefingRoom ||
                          isDockingSpace ||
                          isLaunchTube ||
                          isRecoveryDeck ||
                          isStable ||
                          isWorkshop
                        ) && systems.some((s) => s.id === system.id);
                      const isGravScreenInstalled =
                        isGravScreen &&
                        systems.some((s) => s.subtype === "internalGravScreen");

                      return (
                        <div
                          key={system.id}
                          className="border rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700 dark:bg-gray-700 min-h-[220px] border-gray-200 dark:border-gray-600"
                        >
                          <div>
                            <div className="flex justify-between items-start mb-2">
                              <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                                {system.name}
                              </h4>
                              <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 px-2 py-1 rounded">
                                TL{system.techLevel}
                              </span>
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                              <div>
                                <b>Cost:</b> {formatCurrency(specs.cost)}
                              </div>
                              <div>
                                <b>Tonnage:</b> {formatWeight(specs.tonnage)}
                              </div>
                              <div>
                                <b>Power:</b> {formatPower(specs.power)}
                              </div>
                              {specs.effect && (
                                <div className="text-blue-700 dark:text-blue-300">
                                  {specs.effect}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="mt-2">
                            {!isInstalled && !isGravScreenInstalled ? (
                              <button
                                onClick={() => {
                                  let systemToAdd = system;
                                  if (system.subtype === "internalArmoury") {
                                    const tonsCrew = Math.ceil(
                                      assignedCrew / 25,
                                    );
                                    const tonsMarines = Math.ceil(
                                      assignedMarines / 5,
                                    );
                                    const tons = Math.max(
                                      1,
                                      tonsCrew,
                                      tonsMarines,
                                    );
                                    systemToAdd = {
                                      ...system,
                                      tonnage: tons,
                                      cost: tons * 250000,
                                      power: 0,
                                      properties: {
                                        ...system.properties,
                                        tonnage: tons,
                                      },
                                    };
                                  }
                                  handleAddSystem(systemToAdd);
                                }}
                                className="px-4 py-1 rounded text-white font-semibold bg-blue-600 hover:bg-blue-700"
                                disabled={isInstalled || isGravScreenInstalled}
                              >
                                Add
                              </button>
                            ) : isInstalled || isGravScreenInstalled ? (
                              <button
                                onClick={() => {
                                  if (isGravScreenInstalled) {
                                    // Remove the installed Grav Screen by finding it and removing it
                                    const installedGravScreen = systems.find(
                                      (s) => s.subtype === "internalGravScreen",
                                    );
                                    if (installedGravScreen) {
                                      handleRemoveSystem(
                                        installedGravScreen.id,
                                      );
                                    }
                                  } else {
                                    handleRemoveSystem(system.id);
                                  }
                                }}
                                className="px-4 py-1 rounded text-white font-semibold bg-red-600 hover:bg-red-700"
                              >
                                Remove
                              </button>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            },
          );
        })()}

        {renderConfigurationPanel()}
      </div>
    </div>
  );
};

// Configuration Panels
interface ConfigProps {
  system: InternalSystem;
  onConfigure: (system: InternalSystem) => void;
  onCancel: () => void;
}

const AirlockConfig: React.FC<ConfigProps> = ({
  system,
  onConfigure,
  onCancel,
}) => {
  const [tonnage, setTonnage] = useState<number>(
    typeof system.properties?.tonnage === "number"
      ? system.properties.tonnage
      : 1,
  );
  const specs = calculateInternalSystemSpecs(system.subtype, {
    ...system.properties,
    tonnage,
  });
  return (
    <ConfigModal title="Configure Airlock" onCancel={onCancel}>
      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">Size (tons)</label>
        <input
          type="number"
          min={2}
          value={tonnage}
          onChange={(e) => setTonnage(Number(e.target.value))}
          className="w-full border p-2 rounded"
        />
      </div>
      <div className="flex space-x-3">
        <button
          onClick={() =>
            onConfigure({
              ...system,
              tonnage: tonnage,
              cost: specs.cost,
              power: specs.power,
              properties: { ...system.properties, tonnage },
            })
          }
          className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Add System
        </button>
        <button
          onClick={onCancel}
          className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </ConfigModal>
  );
};

const ArmouryConfig: React.FC<ConfigProps> = ({
  system,
  onConfigure,
  onCancel,
}) => {
  // Get assigned crew count using the correct selector
  const assignedCrew = useAppSelector(
    selectors.derived.selectAssignedCrewCount,
  );
  const crewOverrides = useAppSelector(
    selectors.shipBuilder.selectCrewOverrides,
  );
  const assignedMarines = crewOverrides?.["Ship's Troops/Marines"] ?? 0;
  const tonsCrew = Math.ceil(assignedCrew / 25);
  const tonsMarines = Math.ceil(assignedMarines / 5);
  const tons = Math.max(1, tonsCrew, tonsMarines);
  const cost = tons * 250000;
  const power = 0;
  return (
    <ConfigModal title="Configure Armoury" onCancel={onCancel}>
      <div className="mb-4">
        <div className="text-sm text-gray-700 mb-2">
          Tonnage and cost are automatically calculated based on assigned crew
          or marines.
        </div>
        <div className="mb-2">
          <b>Tonnage:</b> {tons} tons
        </div>
        <div className="mb-2">
          <b>Cost:</b> {formatCurrency(cost)}
        </div>
        <div className="mb-2">
          <b>Power:</b> {power}
        </div>
      </div>
      <div className="flex space-x-3">
        <button
          onClick={() =>
            onConfigure({
              ...system,
              tonnage: tons,
              cost,
              power,
              properties: { ...system.properties, tonnage: tons },
            })
          }
          className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Add System
        </button>
        <button
          onClick={onCancel}
          className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </ConfigModal>
  );
};

const BoobyTrapConfig: React.FC<
  ConfigProps & { shipTonnage: number; installedSystems: InternalSystem[] }
> = ({ system, onConfigure, onCancel, shipTonnage, installedSystems }) => {
  const additionalAirlocks = useMemo(
    () =>
      installedSystems.filter((s) => s.subtype === "internalAirlock").length,
    [installedSystems],
  );
  const totalAirlocks = useMemo(
    () => Math.ceil(shipTonnage / 100) + additionalAirlocks,
    [shipTonnage, additionalAirlocks],
  );
  const [trappedCount, setTrappedCount] = useState<number>(1);
  const [boobyTrapType, setBoobyTrapType] = useState<string>(
    typeof system.properties?.boobyTrapType === "string"
      ? system.properties.boobyTrapType
      : "basic",
  );
  const types = Array.isArray(system.properties?.types)
    ? system.properties.types
    : [];
  const trap = types.find(
    (t: any) => t.label.toLowerCase() === boobyTrapType.toLowerCase(),
  ) ||
    types[0] || { cost: 100000, damage: "3D" };
  const totalCost = trap.cost * trappedCount;
  return (
    <ConfigModal title="Configure Booby-Trapped Airlock" onCancel={onCancel}>
      <div className="mb-2 text-sm text-gray-700">
        This ship has <b>{totalAirlocks}</b> airlock(s) (1 per 100 tons, plus
        any additional airlocks).
      </div>
      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">
          Number of airlocks to trap
        </label>
        <input
          type="number"
          min={1}
          max={totalAirlocks}
          value={trappedCount}
          onChange={(e) =>
            setTrappedCount(
              Math.max(
                1,
                Math.min(totalAirlocks, Math.floor(Number(e.target.value))),
              ),
            )
          }
          className="w-full border p-2 rounded"
        />
      </div>
      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">Trap Type</label>
        <select
          value={boobyTrapType}
          onChange={(e) => setBoobyTrapType(e.target.value)}
          className="w-full border p-2 rounded"
        >
          {types.map((type: any) => (
            <option key={type.label} value={type.label.toLowerCase()}>
              {type.label} (TL{type.tl}, {formatCurrency(type.cost)},{" "}
              {type.damage} dmg)
            </option>
          ))}
        </select>
      </div>
      <div className="mb-4 text-xs text-gray-600">
        <div>Cost: {formatCurrency(totalCost)}</div>
        <div>
          Effect: Deals {trap.damage} per round <b>per trapped airlock</b> (
          {trappedCount} total)
        </div>
      </div>
      <div className="flex space-x-3">
        <button
          onClick={() =>
            onConfigure({
              ...system,
              cost: totalCost,
              power: 0,
              properties: { ...system.properties, boobyTrapType, trappedCount },
            })
          }
          className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Add System
        </button>
        <button
          onClick={onCancel}
          className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </ConfigModal>
  );
};

const GenericTonnageConfig: React.FC<ConfigProps> = ({
  system,
  onConfigure,
  onCancel,
}) => {
  const minTonnage = system.subtype === "stable" ? 10 : 1;
  const [tonnage, setTonnage] = useState<number>(
    typeof system.properties?.tonnage === "number" &&
      system.properties.tonnage >= minTonnage
      ? system.properties.tonnage
      : minTonnage,
  );
  const specs = calculateInternalSystemSpecs(system.subtype, {
    ...system.properties,
    tonnage,
  });
  return (
    <ConfigModal title={`Configure ${system.name}`} onCancel={onCancel}>
      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">Tonnage</label>
        <input
          type="number"
          min={minTonnage}
          value={tonnage}
          onChange={(e) =>
            setTonnage(Math.max(minTonnage, Number(e.target.value)))
          }
          className="w-full border p-2 rounded"
        />
      </div>
      <div className="flex space-x-3">
        <button
          onClick={() =>
            onConfigure({
              ...system,
              tonnage,
              cost: specs.cost,
              power: specs.power,
              properties: { ...system.properties, tonnage },
            })
          }
          className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Add System
        </button>
        <button
          onClick={onCancel}
          className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </ConfigModal>
  );
};

const BiosphereConfig: React.FC<ConfigProps> = ({
  system,
  onConfigure,
  onCancel,
}) => {
  const [tonnage, setTonnage] = useState<number>(
    typeof system.properties?.tonnage === "number"
      ? system.properties.tonnage
      : 1,
  );
  const specs = calculateInternalSystemSpecs(system.subtype, {
    ...system.properties,
    tonnage,
  });
  return (
    <ConfigModal title="Configure Biosphere" onCancel={onCancel}>
      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">
          Tonnage (each ton eliminates life support for 2 passengers)
        </label>
        <input
          type="number"
          min={1}
          step={1}
          value={tonnage}
          onChange={(e) =>
            setTonnage(Math.max(1, Math.floor(Number(e.target.value))))
          }
          className="w-full border p-2 rounded"
        />
      </div>
      <div className="flex space-x-3">
        <button
          onClick={() =>
            onConfigure({
              ...system,
              tonnage,
              cost: specs.cost,
              power: specs.power,
              properties: { ...system.properties, tonnage },
            })
          }
          className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Add System
        </button>
        <button
          onClick={onCancel}
          className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </ConfigModal>
  );
};

const ConcealedCompartmentConfig: React.FC<
  ConfigProps & { shipTonnage: number }
> = ({ system, onConfigure, onCancel, shipTonnage }) => {
  const maxTonnage = Math.floor(shipTonnage * 0.05);
  const [tonnage, setTonnage] = useState<number>(
    typeof system.properties?.tonnage === "number"
      ? system.properties.tonnage
      : 1,
  );
  const specs = calculateInternalSystemSpecs(system.subtype, {
    ...system.properties,
    tonnage,
  });
  return (
    <ConfigModal title="Configure Concealed Compartment" onCancel={onCancel}>
      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">Tonnage</label>
        <input
          type="number"
          min={1}
          max={maxTonnage}
          value={tonnage}
          onChange={(e) =>
            setTonnage(
              Math.max(
                1,
                Math.min(maxTonnage, Math.floor(Number(e.target.value))),
              ),
            )
          }
          className="w-full border p-2 rounded"
        />
        <div className="text-xs text-gray-500 mt-1">
          Maximum allowed: {maxTonnage} tons (5% of ship tonnage)
        </div>
      </div>
      <div className="flex space-x-3">
        <button
          onClick={() =>
            onConfigure({
              ...system,
              tonnage,
              cost: specs.cost,
              power: specs.power,
              properties: { ...system.properties, tonnage },
            })
          }
          className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Add System
        </button>
        <button
          onClick={onCancel}
          className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </ConfigModal>
  );
};

const DockingSpaceConfig: React.FC<ConfigProps> = ({
  system,
  onConfigure,
  onCancel,
}) => {
  const [vesselSize, setVesselSize] = useState<number>(1);
  const [showSmallCraftModal, setShowSmallCraftModal] = useState<boolean>(false);
  const [configuredSystem, setConfiguredSystem] = useState<any>(null);

  // Calculate required docking space tonnage: vessel size + 10%
  // Fix floating-point precision issue: use integer math to avoid 100 * 1.1 = 110.00000000000001
  const requiredDockingSpace = Math.ceil((vesselSize * 11) / 10);
  const specs = calculateInternalSystemSpecs(system.subtype, {
    ...system.properties,
    tonnage: requiredDockingSpace,
  });

  const handleConfigure = () => {
    const configuredSys = {
      ...system,
      tonnage: requiredDockingSpace,
      cost: specs.cost,
      power: specs.power,
      properties: {
        ...system.properties,
        tonnage: requiredDockingSpace,
      },
    };
    setConfiguredSystem(configuredSys);
    setShowSmallCraftModal(true);
  };

  const handleSmallCraftModalClose = () => {
    setShowSmallCraftModal(false);
    setConfiguredSystem(null);
    onCancel();
  };

  const handleSmallCraftAssigned = () => {
    // Close modal after craft is assigned
    setShowSmallCraftModal(false);
    setConfiguredSystem(null);
    onCancel();
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSkipSmallCraft = () => {
    // Add system without small craft assignment
    onConfigure(configuredSystem);
    setShowSmallCraftModal(false);
    setConfiguredSystem(null);
  };

  if (showSmallCraftModal && configuredSystem) {
    return (
      <SmallCraftAssignmentModal
        systemId={configuredSystem.id}
        systemName={`${system.name} (${requiredDockingSpace} tons)`}
        systemType="internal"
        capacity={vesselSize}
        configuredSystem={configuredSystem}
        onClose={handleSmallCraftModalClose}
        onCraftAssigned={handleSmallCraftAssigned}
        onAddSystem={onConfigure}
      />
    );
  }

  return (
    <ConfigModal title="Configure Docking Space" onCancel={onCancel}>
      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">
          Vehicle/Vessel Size (tons)
        </label>
        <input
          type="number"
          min={1}
          value={vesselSize}
          onChange={(e) =>
            setVesselSize(Math.max(1, Math.floor(Number(e.target.value))))
          }
          className="w-full border p-2 rounded"
        />
        <div className="mt-2 text-xs text-gray-700">
          Required docking space: {requiredDockingSpace} tons (vessel size +
          10%)
        </div>
        <div className="mb-2 mt-2">
          <b>Tonnage:</b> {requiredDockingSpace} tons
        </div>
        <div className="mb-2">
          <b>Cost:</b> {formatCurrency(specs.cost)}
        </div>
      </div>
      <div className="flex space-x-3">
        <button
          onClick={handleConfigure}
          className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Add System & Assign Craft
        </button>
        <button
          onClick={onCancel}
          className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
      <div className="mt-2 text-xs text-gray-600">
        You can assign small craft later in the Cargo Configuration step if you skip now.
      </div>
    </ConfigModal>
  );
};

const FullHangarConfig: React.FC<ConfigProps> = ({
  system,
  onConfigure,
  onCancel,
}) => {
  const [craftTonnage, setCraftTonnage] = useState<number>(1);
  const [showSmallCraftModal, setShowSmallCraftModal] = useState<boolean>(false);
  const [configuredSystem, setConfiguredSystem] = useState<any>(null);

  // Calculate required hangar tonnage: craft tonnage × 2
  const requiredHangarTonnage = craftTonnage * 2;
  const specs = calculateInternalSystemSpecs(system.subtype, {
    ...system.properties,
    tonnage: requiredHangarTonnage,
  });

  const handleConfigure = () => {
    const configuredSys = {
      ...system,
      tonnage: requiredHangarTonnage,
      cost: specs.cost,
      power: specs.power,
      properties: {
        ...system.properties,
        tonnage: requiredHangarTonnage,
      },
    };
    setConfiguredSystem(configuredSys);
    setShowSmallCraftModal(true);
  };

  const handleSmallCraftModalClose = () => {
    setShowSmallCraftModal(false);
    setConfiguredSystem(null);
    onCancel();
  };

  const handleSmallCraftAssigned = () => {
    // Close modal after craft is assigned
    setShowSmallCraftModal(false);
    setConfiguredSystem(null);
    onCancel();
  };

  if (showSmallCraftModal && configuredSystem) {
    return (
      <SmallCraftAssignmentModal
        systemId={configuredSystem.id}
        systemName={`${system.name} (${requiredHangarTonnage} tons)`}
        systemType="internal"
        capacity={craftTonnage}
        configuredSystem={configuredSystem}
        onClose={handleSmallCraftModalClose}
        onCraftAssigned={handleSmallCraftAssigned}
        onAddSystem={onConfigure}
      />
    );
  }

  return (
    <ConfigModal title="Configure Full Hangar" onCancel={onCancel}>
      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">
          Total Tonnage of Craft to Contain
        </label>
        <input
          type="number"
          min={1}
          value={craftTonnage}
          onChange={(e) =>
            setCraftTonnage(Math.max(1, Math.floor(Number(e.target.value))))
          }
          className="w-full border p-2 rounded"
        />
        <div className="mt-2 text-xs text-gray-700">
          Required hangar space: {requiredHangarTonnage} tons (craft tonnage ×
          2)
        </div>
        <div className="mb-2 mt-2">
          <b>Tonnage:</b> {requiredHangarTonnage} tons
        </div>
        <div className="mb-2">
          <b>Cost:</b> {formatCurrency(specs.cost)}
        </div>
      </div>
      <div className="flex space-x-3">
        <button
          onClick={handleConfigure}
          className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Add System & Assign Craft
        </button>
        <button
          onClick={onCancel}
          className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
      <div className="mt-2 text-xs text-gray-600">
        You can assign small craft later in the Cargo Configuration step if you skip now.
      </div>
    </ConfigModal>
  );
};

const LaboratoryConfig: React.FC<ConfigProps> = ({
  system,
  onConfigure,
  onCancel,
}) => {
  const [scientists, setScientists] = useState<number>(1);
  const tons = Math.max(1, scientists * 4);
  const cost = Math.ceil(tons / 4) * 1000000;
  return (
    <ConfigModal title="Configure Laboratory" onCancel={onCancel}>
      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">
          Number of Scientists
        </label>
        <input
          type="number"
          min={1}
          value={scientists}
          onChange={(e) =>
            setScientists(Math.max(1, Math.floor(Number(e.target.value))))
          }
          className="w-full border p-2 rounded"
        />
        <div className="mt-2 text-xs text-gray-700">
          Each scientist requires 4 tons. Cost: Cr1,000,000 per 4 tons.
        </div>
        <div className="mb-2 mt-2">
          <b>Tonnage:</b> {tons} tons
        </div>
        <div className="mb-2">
          <b>Cost:</b> {formatCurrency(cost)} Cr
        </div>
      </div>
      <div className="flex space-x-3">
        <button
          onClick={() =>
            onConfigure({
              ...system,
              tonnage: tons,
              cost,
              properties: { ...system.properties, scientists, tonnage: tons },
            })
          }
          className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Add System
        </button>
        <button
          onClick={onCancel}
          className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </ConfigModal>
  );
};

// Dedicated config for Launch Tube
const LaunchTubeConfig: React.FC<ConfigProps> = ({
  system,
  onConfigure,
  onCancel,
}) => {
  const [vesselSize, setVesselSize] = useState<number>(1);
  const tonnage = vesselSize * 10; // 10 times the vessel size
  const cost = tonnage * 500000; // MCr0.5 per ton
  const power = tonnage * 1; // 1 power per ton

  return (
    <ConfigModal title="Configure Launch Tube" onCancel={onCancel}>
      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">
          Size of the largest vessel to be launched from this tube (tons)
        </label>
        <input
          type="number"
          min={1}
          value={vesselSize}
          onChange={(e) =>
            setVesselSize(Math.max(1, Math.floor(Number(e.target.value))))
          }
          className="w-full border p-2 rounded"
        />
        <div className="mt-2 text-xs text-gray-700">
          Launch tube tonnage = 10 × vessel size. Cost: MCr0.5 per ton. Power: 1
          per ton.
        </div>
        <div className="mb-2 mt-2">
          <b>Launch Tube tonnage:</b> {tonnage} tons
        </div>
        <div className="mb-2">
          <b>Cost:</b> {formatCurrency(cost)} Cr
        </div>
        <div className="mb-2">
          <b>Power:</b> {formatPower(power)}
        </div>
      </div>
      <div className="flex space-x-3">
        <button
          onClick={() =>
            onConfigure({
              ...system,
              tonnage,
              cost,
              power,
              properties: {
                ...system.properties,
                vesselSize,
                tonnage,
                launchTubeMaxSize: vesselSize,
              },
            })
          }
          className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Add System
        </button>
        <button
          onClick={onCancel}
          className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </ConfigModal>
  );
};

// Dedicated config for Construction Deck
const ConstructionDeckConfig: React.FC<ConfigProps> = ({
  system,
  onConfigure,
  onCancel,
}) => {
  const [deckSize, setDeckSize] = useState<number>(1);
  const tonnage = deckSize; // Construction deck tonnage is user-defined
  const cost = tonnage * 500000; // MCr0.5 per ton
  const power = tonnage * 1; // 1 power per ton
  // Fix floating-point precision issue: use integer math to avoid calculation errors
  const buildCapacity = Math.floor((tonnage * 5) / 10); // Can build up to half the deck's tonnage

  return (
    <ConfigModal title="Configure Construction Deck" onCancel={onCancel}>
      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">
          Construction Deck Size (tons)
        </label>
        <input
          type="number"
          min={1}
          value={deckSize}
          onChange={(e) =>
            setDeckSize(Math.max(1, Math.floor(Number(e.target.value))))
          }
          className="w-full border p-2 rounded"
        />
        <div className="mt-2 text-xs text-gray-700">
          Construction deck can build/repair ships up to half its tonnage. Cost: MCr0.5 per ton. Power: 1 per ton.
        </div>
        <div className="mb-2 mt-2">
          <b>Construction Deck tonnage:</b> {tonnage} tons
        </div>
        <div className="mb-2">
          <b>Build Capacity:</b> {buildCapacity} tons
        </div>
        <div className="mb-2">
          <b>Cost:</b> {formatCurrency(cost)} Cr
        </div>
        <div className="mb-2">
          <b>Power:</b> {formatPower(power)}
        </div>
      </div>
      <div className="flex space-x-3">
        <button
          onClick={() =>
            onConfigure({
              ...system,
              tonnage,
              cost,
              power,
              properties: {
                ...system.properties,
                deckSize,
                tonnage,
                buildCapacity: buildCapacity,
              },
            })
          }
          className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Add System
        </button>
        <button
          onClick={onCancel}
          className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </ConfigModal>
  );
};

// Dedicated config for Recovery Deck
const RecoveryDeckConfig: React.FC<ConfigProps> = ({
  system,
  onConfigure,
  onCancel,
}) => {
  const [vesselSize, setVesselSize] = useState<number>(1);
  const tonnage = vesselSize * 10; // 10 times the vessel size
  const cost = tonnage * 500000; // MCr0.5 per ton
  const power = tonnage * 1; // 1 power per ton

  return (
    <ConfigModal title="Configure Recovery Deck" onCancel={onCancel}>
      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">
          Size of the largest craft to be recovered (tons)
        </label>
        <input
          type="number"
          min={1}
          value={vesselSize}
          onChange={(e) =>
            setVesselSize(Math.max(1, Math.floor(Number(e.target.value))))
          }
          className="w-full border p-2 rounded"
        />
        <div className="mt-2 text-xs text-gray-700">
          Recovery deck tonnage = 10 × craft size. Cost: MCr0.5 per ton. Power:
          1 per ton.
        </div>
        <div className="mb-2 mt-2">
          <b>Recovery Deck tonnage:</b> {tonnage} tons
        </div>
        <div className="mb-2">
          <b>Cost:</b> {formatCurrency(cost)} Cr
        </div>
        <div className="mb-2">
          <b>Power:</b> {formatPower(power)}
        </div>
      </div>
      <div className="flex space-x-3">
        <button
          onClick={() =>
            onConfigure({
              ...system,
              tonnage,
              cost,
              power,
              properties: {
                ...system.properties,
                vesselSize,
                tonnage,
                recoveryDeckMaxSize: vesselSize,
              },
            })
          }
          className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Add System
        </button>
        <button
          onClick={onCancel}
          className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </ConfigModal>
  );
};

// Dedicated config for Studio
const StudioConfig: React.FC<ConfigProps> = ({
  system,
  onConfigure,
  onCancel,
}) => {
  const tonsPerUser = Number(system.properties.tonsPerUser) || 4;
  const costPerTon = Number(system.properties.costPerTon) || 100000;
  const minPeople = 1;
  const [people, setPeople] = useState<number>(minPeople);
  const tonnage = Math.max(tonsPerUser * people, tonsPerUser);
  const cost = tonnage * costPerTon;
  return (
    <ConfigModal title="Configure Studio" onCancel={onCancel}>
      <>
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Number of Users
          </label>
          <input
            type="number"
            min={minPeople}
            value={people}
            onChange={(e) =>
              setPeople(Math.max(minPeople, Math.floor(Number(e.target.value))))
            }
            className="w-full border p-2 rounded"
          />
          <div className="mt-2 text-xs text-gray-700">
            Each user requires {tonsPerUser} tons. Cost:{" "}
            {costPerTon.toLocaleString()} Cr per ton.
          </div>
          <div className="mb-2 mt-2">
            <b>Tonnage:</b> {tonnage} tons
          </div>
          <div className="mb-2">
            <b>Cost:</b> {cost.toLocaleString()} Cr
          </div>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() =>
              onConfigure({
                ...system,
                tonnage,
                cost,
                properties: { ...system.properties, people, tonnage },
              })
            }
            className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Add System
          </button>
          <button
            onClick={onCancel}
            className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
          >
            Cancel
          </button>
        </div>
      </>
    </ConfigModal>
  );
};

// Dedicated config for Training Facilities
const TrainingFacilitiesConfig: React.FC<ConfigProps> = ({
  system,
  onConfigure,
  onCancel,
}) => {
  const tonsPerTrainee = Number(system.properties.tonsPerTrainee) || 2;
  const costPerTon = Number(system.properties.costPerTon) || 200000;
  const minPeople = 1;
  const [people, setPeople] = useState<number>(minPeople);
  const tonnage = Math.max(tonsPerTrainee * people, tonsPerTrainee);
  const cost = tonnage * costPerTon;
  return (
    <ConfigModal title="Configure Training Facilities" onCancel={onCancel}>
      <>
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">
            Number of Trainees
          </label>
          <input
            type="number"
            min={minPeople}
            value={people}
            onChange={(e) =>
              setPeople(Math.max(minPeople, Math.floor(Number(e.target.value))))
            }
            className="w-full border p-2 rounded"
          />
          <div className="mt-2 text-xs text-gray-700">
            Each trainee requires {tonsPerTrainee} tons. Cost:{" "}
            {costPerTon.toLocaleString()} Cr per ton.
          </div>
          <div className="mb-2 mt-2">
            <b>Tonnage:</b> {tonnage} tons
          </div>
          <div className="mb-2">
            <b>Cost:</b> {cost.toLocaleString()} Cr
          </div>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() =>
              onConfigure({
                ...system,
                tonnage,
                cost,
                properties: { ...system.properties, people, tonnage },
              })
            }
            className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Add System
          </button>
          <button
            onClick={onCancel}
            className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
          >
            Cancel
          </button>
        </div>
      </>
    </ConfigModal>
  );
};

// Dedicated config for Vault
const VaultConfig: React.FC<ConfigProps> = ({
  system,
  onConfigure,
  onCancel,
}) => {
  const minTonnage = Number(system.properties.minTonnage) || 4;
  const maxTonnage = Number(system.properties.maxTonnage) || 40;
  const costPerTon = Number(system.properties.costPerTon) || 500000;
  const [tonnage, setTonnage] = useState<number>(minTonnage);
  const cost = tonnage * costPerTon;
  return (
    <ConfigModal title="Configure Vault" onCancel={onCancel}>
      <>
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Tonnage</label>
          <input
            type="number"
            min={minTonnage}
            max={maxTonnage}
            value={tonnage}
            onChange={(e) =>
              setTonnage(
                Math.max(
                  minTonnage,
                  Math.min(maxTonnage, Math.floor(Number(e.target.value))),
                ),
              )
            }
            className="w-full border p-2 rounded"
          />
          <div className="mt-2 text-xs text-gray-700">
            Must be between {minTonnage} and {maxTonnage} tons. Cost:{" "}
            {costPerTon.toLocaleString()} Cr per ton.
          </div>
          <div className="mb-2 mt-2">
            <b>Tonnage:</b> {tonnage} tons
          </div>
          <div className="mb-2">
            <b>Cost:</b> {cost.toLocaleString()} Cr
          </div>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() =>
              onConfigure({
                ...system,
                tonnage,
                cost,
                properties: { ...system.properties, tonnage },
              })
            }
            className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Add System
          </button>
          <button
            onClick={onCancel}
            className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
          >
            Cancel
          </button>
        </div>
      </>
    </ConfigModal>
  );
};

const ConfigModal: React.FC<{
  title: string;
  onCancel: () => void;
  children: React.ReactNode;
}> = ({ title, onCancel, children }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
      <h3 className="text-lg font-semibold mb-4">{title}</h3>
      {children}
    </div>
  </div>
);

export default InternalSystems;
